import { useState, useEffect } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { fetcher } from "../lib/api"
import { Button, Input, Label, Dialog, DialogTitle, Select } from "./ui"
import { useToast } from "./toast"

type Column = { id: string; name: string }

/** Quick-create card dialog. Lets you pick a column and type a title to add a card to the board. */
export function NewCardDialog({
  open,
  onClose,
  boardId,
  defaultColumnId,
}: {
  open: boolean
  onClose: () => void
  boardId: string
  defaultColumnId?: string
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [title, setTitle] = useState("")
  const [columnId, setColumnId] = useState(defaultColumnId || "")

  const { data: board } = useQuery({
    queryKey: ["board", boardId],
    queryFn: () => fetcher<{ columns: Column[] }>(`/api/boards/${boardId}`),
    enabled: open && !!boardId,
  })

  const columns: Column[] = board?.columns || []

  useEffect(() => {
    if (open) {
      setTitle("")
      setColumnId(defaultColumnId || columns[0]?.id || "")
    }
  }, [open, defaultColumnId, columns.length])

  useEffect(() => {
    if (!columnId && columns.length > 0) {
      setColumnId(columns[0].id)
    }
  }, [columns, columnId])

  const createCard = useMutation({
    mutationFn: (body: { boardId: string; columnId: string; title: string }) =>
      fetcher("/api/cards", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] })
      toast("Card created", "success")
      onClose()
    },
    onError: (err: any) => toast(err.message || "Failed to create card", "error"),
  })

  return (
    <Dialog open={open} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (!title.trim() || !columnId) return
          createCard.mutate({ boardId, columnId, title: title.trim() })
        }}
        className="space-y-4"
      >
        <DialogTitle>New card</DialogTitle>
        <div>
          <Label>Title</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Card title"
            autoFocus
            required
          />
        </div>
        {columns.length > 1 && (
          <div>
            <Label>Column</Label>
            <Select
              value={columnId}
              onChange={(e) => setColumnId(e.target.value)}
              options={columns.map((c) => ({ value: c.id, label: c.name }))}
            />
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" isLoading={createCard.isPending} disabled={!title.trim() || !columnId}>Add card</Button>
        </div>
      </form>
    </Dialog>
  )
}
